import React from "react";
import { Card, Icon, Avatar, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { httpToHttps } from "../../../utils/images";
import Button from "../../../components/Button";
import { getCountry } from "../../../utils/data/static/countries";

import styled from "styled-components/macro";

const AccountCard = styled(Card)`
  max-width: 169px;
  width: 100%;
  margin-top: 10px !important;
  display: flex;
  flex-wrap: wrap;
  &:not(:last-of-type) {
    margin-right: 10px;
  }
  &:hover {
    box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.2);
  }
  transition: 0.3s ease;
  .ant-card-cover {
    height: 120px;
    display: flex;
    justify-content: center;
    img {
      width: 100%;
      max-width: 138px;
    }
  }
  .cover-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .ant-card-body {
    padding: 0;
    flex: 1 1 100%;
    min-height: 70px;
    border-top: 1px solid #e8e8e8;
  }
  .ant-card-cover {
    flex: 1 1 100%;
  }
`;

const ConnectedIcon = styled(Icon)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: auto;
  font-size: 20px;
  animation: popUp 0.5s ease;
  @keyframes popUp {
    0% {
      transform: scale(0);
    }

    100% {
      transform: scale(1);
    }
  }
`;

const ConnectedTo = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  margin: 10px 0;
  font-size: 12px;
  flex-direction: column;
  align-items: center;
  .ant-avatar {
    margin-right: 8px;
  }
`;

const CardFooter = styled.div`
  background-color: #fafafa;
  display: flex;
  align-items: center;
  padding: 10px;
  justify-content: center;
  position: relative;
  flex-wrap: wrap;
  height: 100%;
  .btn-connect {
    min-width: 100px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    padding: 0;
    &.ant-btn-loading {
      padding-right: 10px;
    }
  }
`;

const ProviderCountry = styled.div`
  position: absolute;
  top: -12px;

  width: 24px;
  height: 24px;

  background-color: #fafafa;
  border-radius: 50%;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 140%;
    height: 140%;
    object-fit: cover;
  }
`;

const TooltipContainer = styled.article`
  text-align: center;
`;

const Account = ({
  account,
  provider,
  logo,
  onConnect,
  onDisconnect,
  isLoading,
  isDisabled,
  connectedTo,
  country,
  flag,
  modeOtherIntegrations,
  onModalOpen,
  multipostingForm,
  filled
}) => {
  const isConnected = Boolean(account);
  const { t } = useTranslation();
  return (
    <AccountCard
      cover={
        <div className="cover-wrapper">
          <img src={httpToHttps(logo)} alt={`${provider} logo`} />
          {modeOtherIntegrations && isConnected ? (
            <Tooltip
              placement="top"
              title={
                <TooltipContainer>
                  <span>
                    {t(
                      "opportunity:wizard.multiposting.integrations.connectedTo"
                    )}
                  </span>
                  <div className="name-wrapper">
                    <strong>
                      {`${connectedTo.name} ${
                        country === "INT" ? "" : getCountry(country)
                      }`.trim()}
                    </strong>
                  </div>
                </TooltipContainer>
              }
            >
              <ConnectedIcon
                data-test={`${provider}-connected-icon`}
                type="check-circle"
                theme="twoTone"
                twoToneColor="#3D64FF"
              />
            </Tooltip>
          ) : isConnected ? (
            <ConnectedIcon
              data-test={`${provider}-connected-icon`}
              type="check-circle"
              theme="twoTone"
              twoToneColor="#3D64FF"
            />
          ) : null}
        </div>
      }
      data-test={`${provider}-card`}
    >
      <CardFooter isConnected={isConnected && connectedTo}>
        {flag && (
          <Tooltip
            placement="top"
            title={
              <TooltipContainer>
                <div className="name-wrapper">{getCountry(country)}</div>
              </TooltipContainer>
            }
          >
            <ProviderCountry>
              <img src={flag} alt={country} />
            </ProviderCountry>
          </Tooltip>
        )}
        {isConnected && connectedTo && !modeOtherIntegrations && (
          <ConnectedTo data-test={`${provider}-account`}>
            <span>
              {t("opportunity:wizard.multiposting.integrations.connectedTo")}
            </span>
            <div className="name-wrapper">
              {connectedTo.avatar && (
                <Avatar size={18} shape="square" src={connectedTo.avatar} />
              )}
              <strong>{connectedTo.name}</strong>
            </div>
          </ConnectedTo>
        )}
        {modeOtherIntegrations && multipostingForm ? (
          <Button
            data-test="url-modal-button"
            disabled={isDisabled}
            loading={isLoading}
            type="quaternary"
            size="sm"
            onClick={onModalOpen}
            className="btn-connect"
            icon={isConnected || filled ? "edit" : undefined}
          >
            {isConnected || filled
              ? t("global:edit")
              : t("opportunity:wizard.multiposting.integrations.insertUrl")}
          </Button>
        ) : isConnected ? (
          <Button
            data-test="disconnect-account-button"
            disabled={isDisabled}
            loading={isLoading}
            type="quaternary"
            size="sm"
            onClick={onDisconnect}
            className="btn-connect"
          >
            {t("global:disconnect")}
          </Button>
        ) : (
          <Button
            data-test="connect-account-button"
            disabled={isDisabled}
            loading={isLoading}
            size="sm"
            onClick={onConnect}
            className="btn-connect"
          >
            {isLoading ? t("global:connecting") : t("global:connect")}
          </Button>
        )}
      </CardFooter>
    </AccountCard>
  );
};

export default Account;
